'use client';

import { useEffect, useState } from 'react';
import PrimaryButton from '@/components/shared/PrimaryButton';
import ComixInput from '@/components/shared/ComixInput';
import { pushThemeColor } from '@/lib/theme-color';

interface EditEmailModalProps {
  /** When true the modal is visible. */
  open: boolean;
  /** Comix session id — used to POST the new email to /api/comix/session. */
  sessionId: string | null | undefined;
  /** Email to pre-fill in the input. Updated value is also passed back via `onSaved`. */
  initialEmail: string;
  /** Called when the user closes the modal without saving. */
  onClose: () => void;
  /** Called with the new email string after a successful save. */
  onSaved: (newEmail: string) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Reusable "Edit email" modal. Pre-fills the input with the current email,
 * validates the format, and persists the change via POST /api/comix/session.
 *
 * Designed to be dropped into any screen that has a `sessionId` and wants to
 * let the user update the saved email in-place.
 */
export default function EditEmailModal({
  open,
  sessionId,
  initialEmail,
  onClose,
  onSaved,
}: EditEmailModalProps) {
  const [value, setValue] = useState(initialEmail);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Refresh the input whenever the modal is (re)opened with a new initial email.
  useEffect(() => {
    if (open) {
      setValue(initialEmail);
      setError('');
    }
  }, [open, initialEmail]);

  useEffect(() => {
    if (!open) return;
    return pushThemeColor('#000000');
  }, [open]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  if (!open) return null;

  const handleSave = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) { setError('Please enter your email address'); return; }
    if (!EMAIL_RE.test(trimmed)) { setError('Please enter a valid email address'); return; }
    if (trimmed === initialEmail) { onClose(); return; }

    setSaving(true);
    setError('');
    try {
      // If no sessionId, generate one for this user
      let finalSessionId = sessionId;
      if (!finalSessionId) {
        // Generate a simple session ID
        finalSessionId = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      }

      const res = await fetch('/api/comix/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: finalSessionId, userEmail: trimmed }),
      });
      if (!res.ok) {
        setError('Failed to save email. Please try again.');
        return;
      }

      // If we just created a new session, save it to cookies
      if (!sessionId) {
        import('js-cookie').then(({ default: Cookies }) => {
          Cookies.set('comixSessionId', finalSessionId!, { expires: 365 });
        });
      }

      onSaved(trimmed);
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      // Backdrop click → close. Stop propagation BEFORE bubbling out so that any
      // ancestor (e.g. a clickable list row) doesn't get this click too.
      onClick={(e) => { e.stopPropagation(); if (!saving) onClose(); }}
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-3xl w-full max-w-md shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-6 pb-8 pt-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-[20px] font-bold text-[#333333]">Edit email</h3>
            <button
              id="btn-close-edit-email"
              type="button"
              onClick={() => !saving && onClose()}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSave} className="space-y-4">
            <ComixInput
              type="email"
              value={value}
              onChange={(e) => { setValue(e.target.value); setError(''); }}
              autoFocus
              placeholder="Enter your email address"
              required
            />
            {error && (
              <p className="text-sm text-red-600">{error}</p>
            )}
            <PrimaryButton
              id="btn-save-email"
              type="submit"
              disabled={saving}
              className="w-full"
            >
              {saving ? 'Saving...' : 'Save'}
            </PrimaryButton>
          </form>
        </div>
      </div>
    </div>
  );
}
